import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import {
  CafeteriaOrden,
  CafeteriaProducto,
  ModoPago,
  cafeteriaApi,
} from '../lib/api'
import { useToast } from '../components/Toast'

const MONO = { fontFamily: 'JetBrains Mono' } as const

interface CarritoItem {
  producto: CafeteriaProducto
  cantidad: number
}

const MODOS: { value: ModoPago; label: string }[] = [
  { value: 'EFECTIVO', label: 'Efectivo' },
  { value: 'TARJETA', label: 'Tarjeta' },
  { value: 'CARGO_PACIENTE', label: 'Cargo a paciente' },
]

export default function CafeteriaCajero() {
  const { user, logout } = useAuth()
  const { showToast } = useToast()

  const [productos, setProductos] = useState<CafeteriaProducto[]>([])
  const [carrito, setCarrito] = useState<CarritoItem[]>([])
  const [modoPago, setModoPago] = useState<ModoPago>('EFECTIVO')
  const [clienteIdInput, setClienteIdInput] = useState('')
  const [ordenesHoy, setOrdenesHoy] = useState<CafeteriaOrden[]>([])
  const [creando, setCreando] = useState(false)
  const [loading, setLoading] = useState(true)

  async function refresh() {
    setLoading(true)
    try {
      const [prods, ords] = await Promise.all([
        cafeteriaApi.productos(),
        cafeteriaApi.listarOrdenes({ sede_id: user?.sede_id || undefined }),
      ])
      setProductos(prods)
      setOrdenesHoy(ords)
    } catch (err: any) {
      showToast(`Error: ${err.message}`, 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
  }, [])

  const productosPorCategoria = useMemo(() => {
    const m = new Map<string, CafeteriaProducto[]>()
    for (const p of productos) {
      const c = p.categoria || 'Otros'
      const arr = m.get(c) ?? []
      arr.push(p)
      m.set(c, arr)
    }
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]))
  }, [productos])

  function agregar(p: CafeteriaProducto) {
    setCarrito((prev) => {
      const idx = prev.findIndex((it) => it.producto.id === p.id)
      if (idx >= 0) {
        const next = [...prev]
        next[idx] = { ...next[idx], cantidad: next[idx].cantidad + 1 }
        return next
      }
      return [...prev, { producto: p, cantidad: 1 }]
    })
  }

  function setCantidad(producto_id: number, cantidad: number) {
    if (cantidad <= 0) {
      setCarrito((prev) => prev.filter((it) => it.producto.id !== producto_id))
      return
    }
    setCarrito((prev) =>
      prev.map((it) =>
        it.producto.id === producto_id ? { ...it, cantidad } : it,
      ),
    )
  }

  const total = useMemo(
    () =>
      carrito.reduce((acc, it) => acc + Number(it.producto.precio_unit) * it.cantidad, 0),
    [carrito],
  )

  async function crearYCheckout() {
    if (carrito.length === 0) {
      showToast('Carrito vacío', 'error')
      return
    }
    if (modoPago === 'CARGO_PACIENTE' && !clienteIdInput) {
      showToast('cargo a paciente requiere cliente_id', 'error')
      return
    }
    const cliente_id =
      modoPago === 'CARGO_PACIENTE' ? Number(clienteIdInput) : null
    if (modoPago === 'CARGO_PACIENTE' && (!cliente_id || isNaN(cliente_id))) {
      showToast('cliente_id inválido', 'error')
      return
    }
    setCreando(true)
    try {
      const orden = await cafeteriaApi.crearOrden({
        sede_id: user!.sede_id || 1,
        cliente_id,
        modo_pago: modoPago,
        lineas: carrito.map((it) => ({
          producto_id: it.producto.id,
          cantidad: it.cantidad,
        })),
      })
      const conf = await cafeteriaApi.checkout(orden.id)
      const cargoMsg =
        conf.modo_pago === 'CARGO_PACIENTE'
          ? ` · cargo: ${conf.cargo_estado}`
          : ''
      showToast(
        `Orden #${conf.id} confirmada · total $${conf.total}${cargoMsg}`,
        'success',
      )
      setCarrito([])
      setClienteIdInput('')
      await refresh()
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.message
      showToast(typeof detail === 'string' ? detail : 'Error', 'error')
    } finally {
      setCreando(false)
    }
  }

  return (
    <div className="min-h-screen">
      <header
        className="sticky top-0 z-30 px-4 md:px-6 py-3 flex items-center gap-3"
        style={{
          background: 'linear-gradient(120deg,#143C78,#1E1E3C)',
          borderBottom: '1px solid rgba(140,170,230,.18)',
        }}
      >
        <img
          src="/avante-logo-white.png"
          alt="Avante"
          style={{ height: 30 }}
          onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
        />
        <div className="flex-1 min-w-0">
          <h1 className="text-base md:text-lg font-bold leading-tight truncate" style={{ fontFamily: 'Space Grotesk' }}>
            Cafetería — Punto de venta
          </h1>
          <p className="text-xs text-[#94A0CD] truncate">{user?.nombre_completo}</p>
        </div>
        <button onClick={logout} className="text-sm text-[#94A0CD] hover:text-white shrink-0">
          Salir
        </button>
      </header>
      <main className="p-4 mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4" style={{ maxWidth: 1280 }}>
        {/* Catálogo */}
        <section className="lg:col-span-2 space-y-4">
          {loading && <p className="text-gray-500">Cargando catálogo...</p>}
          {!loading && productos.length === 0 && (
            <div className="bg-white rounded-lg shadow p-6 text-center text-sm">
              <p className="font-semibold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Catálogo vacío</p>
              <p className="text-gray-500">El administrador debe cargar los productos de cafetería.</p>
            </div>
          )}
          {productosPorCategoria.map(([cat, prods]) => (
            <div key={cat}>
              <h2 className="text-[11px] uppercase tracking-[0.12em] font-semibold mb-2" style={{ color: '#8B97C8' }}>
                {cat}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {prods.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => agregar(p)}
                    className="bg-white rounded-lg shadow p-3 text-left transition-transform active:scale-95"
                    style={{ minHeight: 76 }}
                  >
                    <p className="font-semibold text-sm leading-tight">{p.nombre}</p>
                    <p className="text-base font-bold mt-1" style={{ ...MONO, color: '#7FD4FF' }}>
                      ${Number(p.precio_unit).toFixed(2)}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </section>

        {/* Carrito */}
        <aside className="bg-white rounded-lg shadow p-4 space-y-3 lg:sticky lg:top-20 self-start">
          <h2 className="font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
            Carrito ({carrito.length})
          </h2>
          {carrito.length === 0 && (
            <p className="text-sm text-gray-500">Toca un producto para agregarlo.</p>
          )}
          {carrito.map((it) => (
            <div key={it.producto.id} className="flex items-center gap-2 text-sm">
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{it.producto.nombre}</p>
                <p className="text-xs text-gray-500" style={MONO}>
                  ${Number(it.producto.precio_unit).toFixed(2)} c/u
                </p>
              </div>
              <input
                type="number"
                min="0"
                step="1"
                value={it.cantidad}
                onChange={(e) =>
                  setCantidad(it.producto.id, Number(e.target.value))
                }
                className="border rounded p-1 w-16 text-right"
                style={{ minHeight: 40 }}
              />
              <button
                onClick={() => setCantidad(it.producto.id, 0)}
                className="px-2"
                style={{ color: '#FF5C7A', minHeight: 40 }}
                title="Quitar"
              >
                ✕
              </button>
            </div>
          ))}

          <div className="border-t pt-3">
            <label className="block text-[11px] uppercase tracking-[0.12em] font-semibold mb-1" style={{ color: '#8B97C8' }}>
              Modo de pago
            </label>
            <select
              value={modoPago}
              onChange={(e) => setModoPago(e.target.value as ModoPago)}
              className="w-full border rounded p-2"
            >
              {MODOS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          {modoPago === 'CARGO_PACIENTE' && (
            <div>
              <label className="block text-[11px] uppercase tracking-[0.12em] font-semibold mb-1" style={{ color: '#8B97C8' }}>
                ID del paciente
              </label>
              <input
                type="number"
                value={clienteIdInput}
                onChange={(e) => setClienteIdInput(e.target.value)}
                placeholder="Ej. 123"
                className="w-full border rounded p-2"
                style={MONO}
              />
              <p className="text-xs text-gray-500 mt-1">
                El paciente debe estar activo y en esta sede (el ID aparece en Cuentas abiertas).
              </p>
            </div>
          )}

          <div className="border-t pt-3 flex justify-between items-center">
            <span className="text-[11px] uppercase tracking-[0.12em] font-semibold" style={{ color: '#8B97C8' }}>Total</span>
            <span className="text-2xl font-bold" style={{ ...MONO, color: '#7FD4FF' }}>
              ${total.toFixed(2)}
            </span>
          </div>

          <button
            onClick={crearYCheckout}
            disabled={creando || carrito.length === 0}
            className="w-full py-3 font-semibold text-white disabled:opacity-50 relative overflow-hidden"
            style={{
              minHeight: 48,
              borderRadius: 16,
              background: carrito.length > 0 ? 'linear-gradient(120deg,#006EB4,#143C78)' : 'rgba(255,255,255,.06)',
              boxShadow: carrito.length > 0 ? '0 14px 30px -10px rgba(0,110,180,.7)' : 'none',
            }}
          >
            {carrito.length > 0 && !creando && (
              <span aria-hidden style={{
                position: 'absolute', top: 0, bottom: 0, width: '40%',
                background: 'linear-gradient(90deg,transparent,rgba(255,255,255,.35),transparent)',
                animation: 'avSheen 3.2s ease-in-out infinite',
              }} />
            )}
            {creando ? 'Procesando...' : `Cobrar $${total.toFixed(2)}`}
          </button>
        </aside>

        {/* Órdenes del día */}
        <section className="lg:col-span-3">
          <h2 className="text-[11px] uppercase tracking-[0.12em] font-semibold mb-2" style={{ color: '#8B97C8' }}>
            Órdenes de hoy ({ordenesHoy.length})
          </h2>
          {ordenesHoy.length === 0 && (
            <div className="bg-white rounded-lg shadow p-4 text-center text-gray-500 text-sm">
              Todavía no hay ventas hoy.
            </div>
          )}
          {ordenesHoy.map((o) => (
            <div
              key={o.id}
              className="bg-white rounded-lg shadow p-3 mb-2 flex justify-between"
            >
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">
                  <span style={MONO}>#{o.id}</span> — {o.modo_pago.replace('_', ' ').toLowerCase()}
                  {o.cliente_nombre && (
                    <span className="text-xs text-gray-500 ml-2">
                      ({o.cliente_nombre})
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-500" style={MONO}>
                  {o.lineas.length} ítems · {new Date(o.created_at).toLocaleTimeString()}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-bold" style={{ ...MONO, color: '#7FD4FF' }}>
                  ${Number(o.total).toFixed(2)}
                </p>
                <p className="text-xs" style={{ color: o.estado === 'CONFIRMADA' ? '#2FE08A' : '#94A0CD' }}>
                  {o.estado.toLowerCase()}
                </p>
              </div>
            </div>
          ))}
        </section>
      </main>
    </div>
  )
}
